import { Box, HStack, Label, Stack, VStack } from '@hover/blueprint';
import type { UseRadioProps } from '@hover/blueprint/chakra';
import { useRadio, useRadioGroup } from '@hover/blueprint/chakra';
import React from 'react';

interface CustomRadioProps extends UseRadioProps {
  render: JSX.Element;
  widthOfCardInPercent: number;
  disabled?: boolean;
  isMobile?: boolean;
}

export const CustomRadio = (props: CustomRadioProps) => {
  const { render, disabled, widthOfCardInPercent, isMobile, ...radioProps } =
    props;
  const { state, getInputProps, getRootProps, htmlProps } =
    useRadio(radioProps);

  const disabledColor = 'neutral.400';
  let borderColor = 'neutral.100';
  if (state.isChecked) borderColor = 'neutral.900';
  else if (disabled) borderColor = disabledColor;

  return (
    <Label
      {...htmlProps}
      cursor="pointer"
      marginBottom={isMobile ? '1rem' : 0}
      width={`${isMobile ? 100 : widthOfCardInPercent}%`}
    >
      <input {...getInputProps({})} disabled={disabled} hidden />
      <Box
        {...getRootProps()}
        _hover={{ borderColor: disabled ? disabledColor : 'neutral.600' }}
        backgroundColor="neutral.0"
        borderColor={borderColor}
        borderRadius="15px"
        borderStyle="solid"
        borderWidth={isMobile ? '1px' : '3px'}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        justifyContent="center"
        minHeight="200px"
      >
        <Box alignItems="center" marginX="1rem">
          {render}
        </Box>
      </Box>
    </Label>
  );
};

interface RadioProp {
  value: string;
  render: JSX.Element;
  disabled?: boolean;
  ref?: React.Ref<any>;
}

interface CardRadioGroupProps {
  options: RadioProp[];
  onChange?: (value: string) => void;
  defaultValue?: string;
  isMobile?: boolean;
  width?: number;
}

export const CardRadioGroup = (props: CardRadioGroupProps) => {
  const { options, onChange, defaultValue, isMobile } = props;
  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange,
    defaultValue,
  });

  const widthOfCardInPercent = options.length > 0 ? 100 / options.length : 100;

  const StackComponent = isMobile ? VStack : HStack;

  return (
    <Stack {...getRootProps()} width={props?.width}>
      <StackComponent spacing={12}>
        {options.map(option => (
          <CustomRadio
            disabled={option.disabled}
            key={option.value}
            render={option.render}
            widthOfCardInPercent={widthOfCardInPercent}
            {...getRadioProps({ value: option.value })}
            isMobile={isMobile}
          />
        ))}
      </StackComponent>
    </Stack>
  );
};
